import React, { useCallback, useEffect, useState, useRef } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import DashboardContent from "../../components/DashboardContent";
import useCsr from "../../hooks/useCsr";
import { ReactComponent as CsrComplete } from "../../assets/images/icons/csr-complete.svg";
import { ReactComponent as CsrPending } from "../../assets/images/icons/csr-pending.svg";
import { ReactComponent as CsrWarning } from "../../assets/images/icons/csr-warning.svg";
import { ReactComponent as CsrError } from "../../assets/images/icons/file-error.svg";
import { ReactComponent as CsrGeneratePending } from "../../assets/images/icons/csr-generate-pending.svg";
import { ReactComponent as CheckCircle } from "../../assets/images/icons/check-circle.svg";
import { ReactComponent as CsrDownload } from "../../assets/images/icons/download-white.svg";
import { ReactComponent as CsrDownloadBlue } from "../../assets/images/icons/download-blue.svg";
import { ReactComponent as Summary } from "../../assets/images/icons/summary.svg";
import { ReactComponent as InfoUnfilled } from "../../assets/images/icons/info-unfilled.svg";
import { ReactComponent as ArrowRight } from "../../assets/images/icons/arrow-right.svg";
import { CsrAPI } from "../../apis/CsrAPI";
import { CSR_STATUS, FILES_STATUS } from "../../utils/Constants";
import { toast, ToastContainer } from "react-toastify";
import * as Sentry from "@sentry/react";

// Only use Sentry in production environment
const isProduction = process.env.NODE_ENV === "production";
import RedirectHandler from "../../components/RedirectHandler";
import { downloadWizardQuestionsPDF } from "../../utils/pdfDownload";
import generatePDF from "react-to-pdf";
import TrafficLightReportsDropdown from "../../components/TrafficLightReportsDropdown";

const CSRFile = () => {
  const targetRef = useRef(null);
  const { studyId } = useParams();
  const navigate = useNavigate();
  const {
    setAjaxLoading,
    errRef,
    errMsg,
    setErrMsg,
    downloadLatestFile,
    getCsrStatus,
    stopStatusPolling,
    getStudyInfo,
    currentStudyName,
    currentRequestId,
    setCurrentRequestId,
    currentRequestTime,
    mainStatus,
    setMainStatus,
    filesStatus,
    questionsStatus,
    currentTrafficLights,
    setCurrentTrafficLights,
    wrongEnvironment,
    documentsList,
  } = useCsr();

  const [showRedirect, setShowRedirect] = useState(false);
  const [redirectToFileUpload, setRedirectToFileUpload] = useState(false);

  useEffect(() => {
    if (studyId && currentRequestId) {
      getCsrStatus(studyId, currentRequestId);
    } else if (studyId) {
      getStudyInfo(studyId);
    }
    return () => {
      stopStatusPolling();
    };
  }, [
    studyId,
    currentRequestId,
    getCsrStatus,
    getStudyInfo,
    stopStatusPolling,
  ]);

  const toPDF = () => {
    generatePDF(targetRef, {
      filename: "Traffic Light Status.pdf",
      page: {
        // margin is in MM, default is Margin.NONE = 0
        margin: {
          top: 10,
          right: 20,
          bottom: 10,
          left: 30,
        },
        // default is 'A4'
        format: "A4",
        // default is 'portrait'
        orientation: "portrait",
      },
    });
  };

  useEffect(() => {
    setCurrentTrafficLights([]);
  }, [studyId, setCurrentTrafficLights]);

  const generateTrafficLights = useCallback(() => {
    const statusMessages = new Array();
    for (const [sectionIndex, section] of currentTrafficLights.entries()) {
      const sectionTitle = section.name;
      statusMessages.push(
        <span
          key={`section-${sectionIndex}`}
          className={"csr-file__info-component__section-label"}>
          {sectionTitle}
        </span>
      );
      for (const [
        subSectionIndex,
        subSection,
      ] of section.subsections.entries()) {
        const component = (
          <div
            key={`section-${sectionIndex}-subsection-${subSectionIndex}`}
            className={"csr-file__info-component"}>
            <div className={"csr-file__info-component__left"}>
              <span className={"csr-file__info-component__status"}>
                {subSection.user_status === "SUCCESS" ? (
                  <CsrComplete />
                ) : subSection.user_status === "WARNING" ? (
                  <CsrWarning />
                ) : (
                  <CsrError />
                )}
              </span>
              <span className={"csr-file__info-component__label"}>
                {subSection.subsection ? subSection.subsection : sectionTitle}
              </span>
            </div>
            {/* <div className={"csr-file__info-component__right"}></div> */}
            <div className={"csr-file__info-component__rightContainer"}>
              <span className={"csr-file__info-component__messages"}>
                {subSection.user_messages.map((message, i) => (
                  <p
                    key={`section-${sectionIndex}-subsection-${subSectionIndex}-message-${i}`}>
                    {message}
                  </p>
                ))}
              </span>
            </div>
          </div>
        );
        statusMessages.push(component);
      }
    }
    return <>{statusMessages}</>;
  }, [currentTrafficLights]);

  const handleCreateLaySummary = () => {
    navigate(`/ls/new/select-study?csr_study_id=${studyId}`);
  };

  const handleRequestCsr = async event => {
    event.preventDefault();

    stopStatusPolling();
    setMainStatus(CSR_STATUS.generating);
    setCurrentTrafficLights([]);

    CsrAPI.requestCsr({ studyId })
      .then(response => {
        setCurrentRequestId(response.request_id);
        getCsrStatus(studyId, response.request_id);
      })
      .catch(error => {
        if (isProduction) {
          Sentry.captureException(error);
        }
        if (!error?.response) {
          setErrMsg("No Server Response");
        } else if (error.response?.status === 401) {
          setErrMsg("Invalid user credentials");
        } else if (error.response?.status === 403) {
          setShowRedirect(true);
        } else if ([409, 412].includes(error.response?.status)) {
          setRedirectToFileUpload(true);
        } else {
          setErrMsg("Fail to request creation of CSR");
        }
        errRef.current?.focus();
      });
  };

  const handleAbortCsr = async event => {
    event.preventDefault();

    setAjaxLoading(true);

    CsrAPI.abortCsr({ studyId, requestId: currentRequestId })
      .then(() => {
        setMainStatus(CSR_STATUS.inProgress);
        toast.success("CSR generation stopped.");
      })
      .catch(error => {
        if (isProduction) {
          Sentry.captureException(error);
        }
        if (!error?.response) {
          setErrMsg("No Server Response");
        } else if (error.response?.status === 401) {
          setErrMsg("Invalid user credentials");
        } else if (error.response?.status === 403) {
          setShowRedirect(true);
        } else {
          setErrMsg("Fail to abort creation of CSR");
        }
        errRef.current?.focus();
      })
      .finally(() => {
        setAjaxLoading(false);
      });
  };

  const handleDownloadCsr = async event => {
    event.preventDefault();
    const fileURL = "/studies/" + studyId + "/csr/download_url";
    downloadLatestFile(studyId, currentStudyName + ".docx", fileURL);
  };

  const handleDownloadWizardQuestions = async event => {
    event.preventDefault();

    try {
      setAjaxLoading(true);
      const questionsData = await CsrAPI.getQuestions({ studyId });
      await downloadWizardQuestionsPDF(questionsData);
      toast.success("Wizard questions and answers downloaded successfully!");
    } catch (error) {
      if (isProduction) {
        Sentry.captureException(error);
      }
      if (!error?.response) {
        setErrMsg("No Server Response");
      } else if (error.response?.status === 401) {
        setErrMsg("Invalid user credentials");
      } else if (error.response?.status === 403) {
        setShowRedirect(true);
      } else {
        setErrMsg("Failed to download wizard questions");
      }
      errRef.current?.focus();
    } finally {
      setAjaxLoading(false);
    }
  };

  const PageContent = () => {
    return (
      <div className={"csr-file csr-file__wrapper"}>
        <span
          ref={errRef}
          className={errMsg ? "errmsg" : "offscreen"}
          aria-live="assertive">
          {errMsg}
        </span>

        {/* Study Name and Status Section */}
        <div className={"csr-file__study-header"}>
          <div className={"csr-file__study-name"}>{currentStudyName}</div>
        </div>

        <div className="csr-file__status-container">
          <div className={"csr-file__status-info"}>
            <InfoUnfilled className={"csr-file__status-icon"} />
            <span>Status: {mainStatus}</span>
          </div>

          {/* Last Generated Section */}
          {mainStatus === CSR_STATUS.ready && currentRequestTime && (
            <div className={"csr-file__last-generated"}>
              <CheckCircle className={"csr-file__check-icon"} />
              <span>Last generated - {currentRequestTime}</span>
            </div>
          )}
        </div>

        {/* Action Buttons */}
        <div className={"csr-file__action-buttons"}>
          {mainStatus === CSR_STATUS.ready ? (
            <>
              <button
                className={
                  "input-submit input-submit__primary input-submit__arrow csr-file__download-button"
                }
                onClick={handleDownloadCsr}>
                <CsrDownload /> Download CSR
              </button>
              <button
                className={
                  "input-submit input-submit__primary input-submit__arrow csr-file__download-button"
                }
                onClick={handleRequestCsr}
                disabled={
                  !(
                    mainStatus &&
                    (filesStatus === FILES_STATUS.ready ||
                      filesStatus === FILES_STATUS.readyEmpty) &&
                    questionsStatus
                  )
                }>
                Generate again <ArrowRight />
              </button>
              <button
                className={
                  "input-submit input-submit__secondary input-submit__small csr-file__lay-summary-button"
                }
                onClick={handleCreateLaySummary}>
                <Summary /> Create lay summary
              </button>
            </>
          ) : mainStatus === CSR_STATUS.generating ? (
            <>
              <button
                className={
                  "input-submit input-submit__primary input-submit__arrow csr-file__download-button"
                }
                disabled={true}>
                Download CSR <CsrDownload />
              </button>
              <div className={"csr-file__generate-component"}>
                <div className={"csr-file__generate-component__left"}>
                  <span className={"csr-file__generate-component__status"}>
                    <CsrGeneratePending />
                  </span>
                  <span className={"csr-file__generate-component__label"}>
                    Generating CSR
                  </span>
                </div>
                <div className={"csr-file__generate-component__right"}>
                  <button
                    className={
                      "input-submit input-submit__tertiary input-submit__small"
                    }
                    onClick={handleAbortCsr}>
                    Stop
                  </button>
                </div>
              </div>
            </>
          ) : (
            <button
              className={
                "input-submit input-submit__primary input-submit__small csr-file__generate-button"
              }
              onClick={handleRequestCsr}
              disabled={
                !(
                  mainStatus &&
                  filesStatus === FILES_STATUS.ready &&
                  (documentsList.csp.length || documentsList.tfl.length) &&
                  questionsStatus
                )
              }>
              Generate CSR file
            </button>
          )}
        </div>

        {/* CSR Content Checklist Card */}
        <div className={"csr-file__card"}>
          <div className={"csr-file__info-components"}>
            <div className={"csr-file__info-component"}>
              <div className={"csr-file__info-component__left"}>
                <span className={"csr-file__info-component__status"}>
                  <CsrComplete />
                </span>
                <span className={"csr-file__info-component__label"}>
                  Summary Name
                </span>
              </div>
              <div className={"csr-file__info-component__right"}></div>
            </div>

            <div className={"csr-file__info-component"}>
              <div className={"csr-file__info-component__left"}>
                <span className={"csr-file__info-component__status"}>
                  {filesStatus === FILES_STATUS.warning ? (
                    <CsrWarning />
                  ) : filesStatus === FILES_STATUS.ready &&
                    (documentsList.csp.length || documentsList.tfl.length) ? (
                    <CsrComplete />
                  ) : (
                    <CsrPending />
                  )}
                </span>
                <span className={"csr-file__info-component__label"}>Files</span>
              </div>
              <div className={"csr-file__info-component__right"}>
                <Link
                  to={"/csr/" + studyId + "/file-upload"}
                  className={"csr-file__info-component__action"}>
                  Edit
                </Link>
              </div>
            </div>

            <div className={"csr-file__info-component"}>
              <div className={"csr-file__info-component__left"}>
                <span className={"csr-file__info-component__status"}>
                  {questionsStatus ? <CsrComplete /> : <CsrWarning />}
                </span>
                <span className={"csr-file__info-component__label"}>
                  Wizard questions
                </span>
              </div>
              <div className={"csr-file__info-component__action-container"}>
                <Link
                  to={"/csr/" + studyId + "/questions"}
                  className={"csr-file__info-component__action"}>
                  {questionsStatus ? "Edit" : "Complete"}
                </Link>
                <button
                  className={
                    "input-submit input-submit__primary input-submit__downloadButtonGroup input-submit__pdf"
                  }
                  onClick={handleDownloadWizardQuestions}
                  disabled={!questionsStatus}>
                  <CsrDownloadBlue /> Download PDF
                </button>
              </div>
            </div>

            <div className={"csr-file__info-component"}>
              <div className={"csr-file__info-component__left"}>
                <span className={"csr-file__info-component__status"}>
                  {questionsStatus ? <CsrComplete /> : <CsrWarning />}
                </span>
                <span className={"csr-file__info-component__label"}>
                  Traffic light status
                </span>
              </div>
              <div className={"csr-file__info-component__right"}>
                <button
                  className={
                    "input-submit input-submit__primary input-submit__downloadButtonGroup input-submit__pdf"
                  }
                  onClick={toPDF}
                  disabled={!questionsStatus}>
                  <CsrDownloadBlue /> Download PDF
                </button>
              </div>
            </div>

            <div className={"csr-file__info-component"}>
              <div className={"csr-file__info-component__left"}>
                <span className={"csr-file__info-component__status"}>
                  {questionsStatus ? <CsrComplete /> : <CsrWarning />}
                </span>
                <span className={"csr-file__info-component__label"}>
                  History
                </span>
              </div>
              <div className={"csr-file__info-component__right"}>
                <TrafficLightReportsDropdown
                  studyId={studyId}
                  disabled={!questionsStatus}
                  mainStatus={mainStatus}
                />
              </div>
            </div>
          </div>
        </div>

        {/* Traffic lights display - includes Adverse Events */}
        {currentTrafficLights.length > 0 && (
          <div className={"csr-file__card csr-file__traffic-lights-card"}>
            {currentTrafficLights.some(
              section =>
                section.name?.toLowerCase().includes("adverse") ||
                section.name?.toLowerCase().includes("safety")
            )}
            <div className={"csr-file__info-components"} ref={targetRef}>
              {generateTrafficLights()}
            </div>
          </div>
        )}

        {redirectToFileUpload && (
          <RedirectHandler
            redirectUrl={"/csr/" + studyId + "/file-upload"}
            message="Please make sure at least a CSP file, or Tables and Figures files, are uploaded and successfully processed, and try again. Redirecting to file upload page..."
          />
        )}
        <ToastContainer
          position="top-right"
          autoClose={2000}
          hideProgressBar={false}
          newestOnTop={false}
          closeOnClick={true}
          rtl={false}
          pauseOnFocusLoss={false}
          draggable
          pauseOnHover
          theme="light"
        />
      </div>
    );
  };

  if (showRedirect || wrongEnvironment) {
    return <RedirectHandler />;
  }

  return (
    <DashboardContent
      sidebarCSR={true}
      headerTitle={"Generate & Download CSR"}
      headerButton={false}
      pageContent={PageContent()}
      prevButton={{
        buttonTitle: "Data Mapping",
        buttonGoToAction: "/csr/" + studyId + "/data-mapping",
      }}
      nextButton={{
        buttonTitle: "TriloAI",
        buttonGoToAction: "/csr/" + studyId + "/advancedsearch",
        disabled: mainStatus !== CSR_STATUS.ready,
      }}
    />
  );
};

export default CSRFile;
